// API para Generación de Cargos Automáticos

const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:8000/api';

/**
 * Obtener lista de campus para dropdown
 * Usa el endpoint existente de financiero
 */
export async function getCampusDropdown() {
  try {
    const token = localStorage.getItem('authToken');
    const response = await fetch(`${API_BASE}/financiero/campus-dropdown/`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      }
    });

    if (!response.ok) {
      throw new Error('Error al obtener campus');
    }

    const data = await response.json();
    // El endpoint devuelve array con { id, nombre }
    return data;
  } catch (error) {
    console.error('Error en getCampusDropdown:', error);
    throw error;
  }
}

/**
 * Obtener conceptos de cobro activos filtrados por campus + ciclo escolar.
 * Reutiliza el endpoint `/financiero/conceptos/` (mismo que Cargos Manuales),
 * que sí aplica los filtros server-side por campus, sección, categoría y ciclo.
 * @param {Object} params - Parámetros de filtro
 * @param {number} params.campusId - ID del campus (requerido para filtrar)
 * @param {string} params.cicloNombre - nombre_ciclo (p.ej. "2026-2") para filtrar conceptos del ciclo
 * @param {number|string} params.cicloId - id_ciclo_escolar (más robusto que el nombre)
 * @param {number} params.seccionId - (opcional) id de sección académica
 */
export async function getConceptosActivos(params = {}) {
  try {
    const token = localStorage.getItem('authToken');
    const queryParams = new URLSearchParams();

    if (params.campusId) {
      queryParams.append('campus', params.campusId);
    }
    if (params.cicloId) {
      // Más robusto: el backend resuelve nombre_ciclo y compara con iexact
      queryParams.append('id_ciclo_escolar', params.cicloId);
    }
    if (params.cicloNombre) {
      queryParams.append('ciclo_escolar', params.cicloNombre);
    }
    if (params.seccionId) {
      queryParams.append('seccion', params.seccionId);
    }

    const url = `${API_BASE}/financiero/cargos-manuales/conceptos/${queryParams.toString() ? `?${queryParams}` : ''}`;
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      }
    });

    if (!response.ok) {
      throw new Error('Error al obtener conceptos');
    }

    const data = await response.json();
    console.log('Conceptos raw:', data, 'para campus:', params.campusId || 'todos', 'ciclo:', params.cicloNombre || 'cualquiera');

    // El endpoint /financiero/conceptos/ devuelve { success, count, conceptos: [...] }
    // donde cada concepto trae { id, nombre, codigo, categoria, subcategoria, precio_base, ... }
    let raw = [];
    if (Array.isArray(data)) {
      raw = data;
    } else if (Array.isArray(data.conceptos)) {
      raw = data.conceptos;
    } else if (Array.isArray(data.results)) {
      raw = data.results;
    }

    // Normalizar a la forma que ya consume la pantalla: { id, codigo, nombre, monto_default, ... }
    const conceptos = raw.map((c) => ({
      id: c.id ?? c.id_concepto,
      codigo: c.codigo ?? c.codigo_concepto ?? '',
      nombre: c.nombre ?? c.nombre_concepto ?? '',
      categoria: c.categoria ?? '',
      subcategoria: c.subcategoria ?? '',
      ciclo: c.ciclo ?? null,
      seccion: c.seccion ?? null,
      seccion_id: c.seccion_id ?? c.id_seccion ?? null,
      monto_default: c.precio_base ?? c.monto ?? 0,
      precio_base: c.precio_base ?? 0,
      precio_anticipado: c.precio_anticipado ?? 0,
    }));

    return {
      success: true,
      conceptos
    };
  } catch (error) {
    console.error('Error en getConceptosActivos:', error);
    throw error;
  }
}

/**
 * Obtener ciclos escolares
 * Usa el endpoint existente de financiero
 */
export async function getCiclosEscolares() {
  try {
    const token = localStorage.getItem('authToken');
    const response = await fetch(`${API_BASE}/financiero/ciclos-dropdown/`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      }
    });

    if (!response.ok) {
      throw new Error('Error al obtener ciclos escolares');
    }

    const data = await response.json();
    // El endpoint devuelve array con { id_ciclo_escolar, ciclo, fecha_inicio, fecha_fin }
    return data;
  } catch (error) {
    console.error('Error en getCiclosEscolares:', error);
    throw error;
  }
}

/**
 * Obtener secciones por campus
 * Usa el endpoint de financiero/cargos-automaticos
 */
export async function getSeccionesDropdown(campusId) {
  try {
    const token = localStorage.getItem('authToken');
    const response = await fetch(`${API_BASE}/financiero/cargos-automaticos/${campusId}/secciones/`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      }
    });

    if (!response.ok) {
      console.error('Error response secciones:', response.status);
      // Devolver array vacío en caso de error para no romper el dropdown
      return [];
    }

    const data = await response.json();
    console.log('Secciones raw:', data);
    // El endpoint devuelve { secciones: [...] }
    const secciones = data.secciones || data || [];
    return secciones.map(seccion => ({
      id: seccion.id_seccion || seccion.id,
      nombre: seccion.nombre_seccion || seccion.nombre
    }));
  } catch (error) {
    console.error('Error en getSeccionesDropdown:', error);
    return []; // Devolver array vacío en lugar de throw
  }
}

/**
 * Obtener estudiantes elegibles para cargos automáticos
 * @param {Object} params - Filtros de búsqueda
 * @returns {Promise<Object>} Lista de estudiantes elegibles
 */
export async function getEstudiantesElegibles(params) {
  try {
    const token = localStorage.getItem('authToken');
    
    // Asegurar que los IDs sean enteros
    const payload = {
      id_ciclo_escolar: parseInt(params.cicloEscolar) || null,
      id_campus: parseInt(params.campus) || null,
      id_seccion: params.seccion ? parseInt(params.seccion) : null,
      id_concepto: parseInt(params.conceptoCobro) || null,
      fecha_aplicacion: params.fechaAplicacion || null
    };
    
    console.log('Payload estudiantes elegibles:', payload);
    
    const response = await fetch(`${API_BASE}/financiero/cargos-automaticos/estudiantes-elegibles/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(payload)
    });

    if (!response.ok) {
      let errorMessage = 'Error al obtener estudiantes elegibles';
      try {
        const errorData = await response.json();
        errorMessage = errorData.error || errorData.message || errorMessage;
      } catch (e) {
        console.error('Error parsing error response:', e);
      }
      throw new Error(errorMessage);
    }

    const data = await response.json();
    console.log('Respuesta estudiantes elegibles:', data);
    console.log('Total estudiantes en respuesta:', data.total_estudiantes);
    console.log('Total con beca:', data.total_con_beca);
    console.log('Concepto aplica becas:', data.concepto_aplica_becas);
    
    return {
      success: true,
      estudiantes: data.estudiantes || [],
      total: data.total_estudiantes || 0,
      duplicados: data.total_duplicados || 0,
      total_con_beca: data.total_con_beca || 0,
      montoTotal: data.monto_total || 0,
      monto_total_original: data.monto_total_original || 0,
      ahorro_por_becas: data.ahorro_por_becas || 0,
      concepto_aplica_becas: data.concepto_aplica_becas || false,
      concepto: data.concepto || null
    };
  } catch (error) {
    console.error('Error en getEstudiantesElegibles:', error);
    throw error;
  }
}

/**
 * Generar cargos automáticos
 * @param {Object} payload - Datos para generar cargos
 * @returns {Promise<Object>} Resultado de la operación
 */
export async function generarCargosAutomaticos(payload) {
  try {
    const token = localStorage.getItem('authToken');
    
    console.log('Generando cargos automáticos con payload:', payload);
    
    const response = await fetch(`${API_BASE}/financiero/cargos-automaticos/generar/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({
        id_ciclo_escolar: payload.cicloEscolar,
        id_campus: payload.campus,
        id_seccion: payload.seccion || null,
        id_concepto: payload.conceptoCobro,
        monto: payload.monto,
        fecha_aplicacion: payload.fechaAplicacion,
        fecha_vencimiento: payload.fechaVencimiento,
        descripcion: payload.descripcion || null,
        estudiantes: payload.estudiantesIds || []
      })
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Error al generar cargos automáticos');
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error en generarCargosAutomaticos:', error);
    throw error;
  }
}

/**
 * Generar y descargar CSV con los estudiantes a procesar
 * @param {Array} estudiantes - Lista de estudiantes
 * @param {Object} filtros - Filtros aplicados
 */
export function generarCSVPrevio(estudiantes, filtros, conceptoNombre, monto) {
  const headers = ['Matrícula', 'Nombre Completo', 'Programa', 'Sección', 'Concepto', 'Monto', 'Fecha Aplicación', 'Fecha Vencimiento'];
  
  const rows = estudiantes.map(est => [
    est.matricula,
    est.nombre,
    est.programa || '',
    est.seccion || '',
    conceptoNombre,
    monto,
    filtros.fechaAplicacion,
    filtros.fechaVencimiento
  ]);
  
  const csvContent = [
    headers.join(','),
    ...rows.map(row => row.map(cell => `"${cell || ''}"`).join(','))
  ].join('\n');
  
  const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  
  const fechaHora = new Date().toISOString().slice(0, 19).replace(/[:-]/g, '');
  link.setAttribute('href', url);
  link.setAttribute('download', `cargos_automaticos_previo_${fechaHora}.csv`);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  
  return true;
}

/**
 * Validar datos antes de generar cargos
 */
export function validarCargosAutomaticos(formData) {
  const errors = [];
  
  if (!formData.cicloEscolar) {
    errors.push('Ciclo escolar es requerido');
  }
  
  if (!formData.campus) {
    errors.push('Campus es requerido');
  }
  
  if (!formData.conceptoCobro) {
    errors.push('Concepto de cobro es requerido');
  }
  
  if (!formData.fechaAplicacion) {
    errors.push('Fecha de aplicación es requerida');
  }
  
  if (!formData.fechaVencimiento) {
    errors.push('Fecha de vencimiento es requerida');
  }
  
  // Validar que fecha de vencimiento sea posterior a fecha de aplicación
  if (formData.fechaAplicacion && formData.fechaVencimiento) {
    const fechaAplicacion = new Date(formData.fechaAplicacion);
    const fechaVencimiento = new Date(formData.fechaVencimiento);
    
    if (fechaVencimiento < fechaAplicacion) {
      errors.push('Fecha de vencimiento debe ser posterior a fecha de aplicación');
    }
  }
  
  return {
    valid: errors.length === 0,
    errors
  };
}
